import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { IconPlayerPlay, IconListDetails } from "@tabler/icons-react";

export interface TranscriptSegment {
    text: string;
    start: number;
    duration: number;
}

interface Paragraph {
    startTime: number;
    segments: TranscriptSegment[];
}

// Format seconds to MM:SS
const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Group segments into paragraphs ending at sentence boundaries
const groupIntoParagraphs = (segments: TranscriptSegment[]): Paragraph[] => {
    if (segments.length === 0) return [];

    const paragraphs: Paragraph[] = [];
    let currentParagraph: Paragraph = { startTime: segments[0].start, segments: [segments[0]] };

    const endsWithSentence = (text: string) => /[.!?]$/.test(text.trim());
    const MAX_PARAGRAPH_DURATION = 45; // Max seconds per paragraph

    for (let i = 1; i < segments.length; i++) {
        const lastSeg = currentParagraph.segments[currentParagraph.segments.length - 1];
        const paragraphDuration = segments[i].start - currentParagraph.startTime;

        // Start new paragraph if: last segment ended with sentence AND (pause > 1s OR duration > max)
        const prevEnd = lastSeg.start + lastSeg.duration;
        const gap = segments[i].start - prevEnd;

        if (endsWithSentence(lastSeg.text) && (gap > 1 || paragraphDuration > MAX_PARAGRAPH_DURATION)) {
            paragraphs.push(currentParagraph);
            currentParagraph = { startTime: segments[i].start, segments: [segments[i]] };
        } else {
            currentParagraph.segments.push(segments[i]);
        }
    }

    // Don't forget the last paragraph
    if (currentParagraph.segments.length > 0) {
        paragraphs.push(currentParagraph);
    }

    return paragraphs;
};

interface TranscriptPanelProps {
    transcript: TranscriptSegment[];
    currentTime: number;
    onSeek?: (time: number) => void;
    maxHeight?: string;
    showHeader?: boolean;
    className?: string;
}

export const TranscriptPanel = ({
    transcript,
    currentTime,
    onSeek,
    maxHeight = '300px',
    showHeader = true,
    className = '',
}: TranscriptPanelProps) => {
    const [autoScroll, setAutoScroll] = useState(true);
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const activeSegmentRef = useRef<HTMLSpanElement>(null);

    // Group transcript into paragraphs
    const paragraphs = useMemo(() => groupIntoParagraphs(transcript), [transcript]);

    // Find active segment index (flat across all segments)
    const activeSegmentIndex = transcript.findIndex((seg, idx) => {
        const nextSeg = transcript[idx + 1];
        const segEnd = nextSeg ? nextSeg.start : seg.start + seg.duration;
        return currentTime >= seg.start && currentTime < segEnd;
    });

    // Pre-compute segment start indices for each paragraph
    const paragraphSegmentOffsets = useMemo(() => {
        let offset = 0;
        return paragraphs.map(para => {
            const startOffset = offset;
            offset += para.segments.length;
            return startOffset;
        });
    }, [paragraphs]);

    // Auto-scroll to active segment - center in transcript container
    useEffect(() => {
        if (autoScroll && activeSegmentRef.current && transcriptContainerRef.current) {
            const container = transcriptContainerRef.current;
            const segment = activeSegmentRef.current;

            // Calculate position to center the segment in the container
            const containerHeight = container.clientHeight;
            const segmentTop = segment.offsetTop - container.offsetTop;
            const segmentHeight = segment.offsetHeight;

            // Scroll so segment is centered
            const scrollTarget = segmentTop - (containerHeight / 2) + (segmentHeight / 2);

            container.scrollTo({
                top: scrollTarget,
                behavior: 'smooth'
            });
        }
    }, [activeSegmentIndex, autoScroll]);

    const handleSeek = useCallback((time: number) => {
        onSeek?.(time);
    }, [onSeek]);

    if (paragraphs.length === 0) {
        return null;
    }

    return (
        <div className={`flex flex-col ${className}`}>
            {/* Header with auto-scroll toggle */}
            {showHeader && (
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200/60">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <IconListDetails className="w-4 h-4" />
                        <span className="font-medium">Transcript</span>
                        <span className="text-xs text-slate-400">{transcript.length} segments</span>
                    </div>
                    <button
                        onClick={() => setAutoScroll(!autoScroll)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${autoScroll
                                ? 'bg-emerald-100/80 text-emerald-700'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200/80'
                            }`}
                    >
                        <IconPlayerPlay className="w-3 h-3" />
                        Auto Scroll {autoScroll ? 'On' : 'Off'}
                    </button>
                </div>
            )}

            {/* Scrollable transcript content */}
            <div
                ref={transcriptContainerRef}
                className="overflow-y-auto pr-2"
                style={{ maxHeight }}
                onMouseEnter={() => setAutoScroll(false)}
            >
                <div className="space-y-4">
                    {paragraphs.map((para, paraIndex) => (
                        <div key={paraIndex} className="group">
                            {/* Paragraph timestamp */}
                            <div className="text-xs font-mono text-slate-400 mb-1">
                                {formatTime(para.startTime)}
                            </div>

                            {/* Paragraph text with individually clickable/highlightable segments */}
                            <p className="text-sm text-slate-600 leading-relaxed">
                                {para.segments.map((seg, segIdx) => {
                                    const globalIdx = paragraphSegmentOffsets[paraIndex] + segIdx;
                                    const isActive = globalIdx === activeSegmentIndex;

                                    return (
                                        <span
                                            key={segIdx}
                                            ref={isActive ? activeSegmentRef : null}
                                            onClick={() => handleSeek(seg.start)}
                                            className={`cursor-pointer transition-all rounded px-0.5 -mx-0.5 ${isActive
                                                    ? 'bg-amber-200/60 text-slate-800'
                                                    : 'hover:bg-slate-100'
                                                }`}
                                        >
                                            {seg.text}{' '}
                                        </span>
                                    );
                                })}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TranscriptPanel;
